/**
 * Story 3 — unit-action data hooks (query + mutations) for a Type Curve
 * Workspace. Built on the shared `request` wrapper.
 *
 * The backend exposes unit actions under the flat workspace route:
 *   `/type_curve_workspaces/:workspace_id/header/unit_actions`
 * built from the workspace's numeric ID.
 *
 * Every mutation runs the **refresh fan-out** on success:
 *   1. re-GET `/unit_actions` → write `unitOverridesAtom` (pill + map hide)
 *   2. bump `tcUnitResolutionRefreshAtom` → dev-areas query + comments re-fetch
 *      (the map polygons are MVT; removed units are hidden client-side via
 *      `removedUnitIdsAtom`, the overlap count comes from the dev-areas re-fetch)
 */
import { useCallback, useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { get as getReq, post, deleteReq } from 'lib/request/request';
import {
  unitOverridesAtom,
  tcUnitResolutionRefreshAtom,
  tcUnitResolutionPendingAtom,
  tcUnitRotationPendingAtom,
} from './type-curve-filters/atoms';
import type {
  UnitOverride,
  UnitOverridesListResponse,
} from 'types/api-responses';
import type { CreateUnitOverrideRequest } from 'types/api-payloads';
import {
  tcWorkspaceNumericId,
  type TypeCurveWorkspaceResponse,
} from 'types/type-curve-workspace';

// Flat unit-actions collection path, built from the workspace's numeric ID.
// Null when there's no active workspace.
const unitActionsPath = (
  workspace: TypeCurveWorkspaceResponse | null
): string | null => {
  const wsId = workspace ? tcWorkspaceNumericId(workspace) : undefined;
  return wsId === undefined
    ? null
    : `/type_curve_workspaces/${wsId}/header/unit_actions`;
};

type MutationResult = { ok: true } | { ok: false; message?: string };

const isOverridesList = (res: unknown): res is UnitOverridesListResponse =>
  typeof res === 'object' && res !== null && 'unitActions' in res;

// GET the workspace's unit actions into `unitOverridesAtom`. Fetches on mount
// and whenever the workspace changes; also returns a `refetch` for the fan-out.
export const useUnitOverridesQuery = (
  workspace: TypeCurveWorkspaceResponse | null
) => {
  const setOverrides = useSetAtom(unitOverridesAtom);
  const basePath = unitActionsPath(workspace);

  const refetch = useCallback(async () => {
    if (!basePath) {
      setOverrides([]);
      return;
    }
    const res = await getReq(basePath, new AbortController());
    if (isOverridesList(res)) {
      setOverrides(res.unitActions);
    }
  }, [basePath, setOverrides]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { refetch };
};

// The shared refresh fan-out: always re-GET the action list; only bump the
// dev-areas/overlap refresh when the action actually changes the overlap graph.
// Rotate (and un-rotate) only change the LTD geometry *direction* — rendered
// client-side from the action list — so they pass `refreshOverlaps: false` to
// skip the redundant dev-areas re-fetch.
const useRefreshFanout = (workspace: TypeCurveWorkspaceResponse | null) => {
  const setOverrides = useSetAtom(unitOverridesAtom);
  const bumpRefresh = useSetAtom(tcUnitResolutionRefreshAtom);
  const basePath = unitActionsPath(workspace);

  return useCallback(
    async ({ refreshOverlaps = true }: { refreshOverlaps?: boolean } = {}) => {
      if (basePath) {
        const res = await getReq(basePath, new AbortController()); // success check
        if (isOverridesList(res)) {
          setOverrides(res.unitActions);
        }
      }
      if (refreshOverlaps) {
        bumpRefresh((n) => n + 1);
      }
    },
    [basePath, setOverrides, bumpRefresh]
  );
};

// POST a remove/rotate action.
export const useCreateUnitOverrideMutation = (
  workspace: TypeCurveWorkspaceResponse | null
) => {
  const fanout = useRefreshFanout(workspace);
  const setPending = useSetAtom(tcUnitResolutionPendingAtom);
  const setRotationPending = useSetAtom(tcUnitRotationPendingAtom);
  const basePath = unitActionsPath(workspace);

  return useCallback(
    async (body: CreateUnitOverrideRequest): Promise<MutationResult> => {
      if (!basePath) return { ok: false };
      // Remove changes the overlap graph → its loader (resolution atom) stays on
      // until the dev-areas re-fetch settles. Rotate is client-side (no
      // re-fetch) → its own atom, cleared here once the action-list re-GET has
      // applied the new direction.
      const refreshOverlaps = body.action !== 'rotate';
      const setBusy = refreshOverlaps ? setPending : setRotationPending;
      setBusy(true);
      const res = await post<UnitOverride, { errors?: { message: string }[] }>(
        basePath,
        body,
        new AbortController()
      );
      if (res.type === 'success') {
        await fanout({ refreshOverlaps });
        // Remove leaves the loader for the dev-areas refresh effect to clear
        // (see useDevelopmentAreasQuery); rotate clears it here.
        if (!refreshOverlaps) setBusy(false);
        return { ok: true };
      }
      setBusy(false);
      return {
        ok: false,
        message:
          res.type === 'error' ? res.data?.errors?.[0]?.message : res.message,
      };
    },
    [basePath, fanout, setPending, setRotationPending]
  );
};

// Remove several units in one go (the drawer's "Keep" CTA). Fires one remove
// POST per unit (sequential, so undo_last_removal order stays deterministic),
// then runs the fan-out ONCE — a single action re-GET + a single dev-areas
// re-fetch — instead of one per unit.
export const useBulkRemoveUnitsMutation = (
  workspace: TypeCurveWorkspaceResponse | null
) => {
  const fanout = useRefreshFanout(workspace);
  const setPending = useSetAtom(tcUnitResolutionPendingAtom);
  const basePath = unitActionsPath(workspace);

  return useCallback(
    async (developmentAreaIds: number[]): Promise<MutationResult> => {
      if (!basePath || developmentAreaIds.length === 0) {
        return { ok: false };
      }
      setPending(true);
      let firstError: string | undefined;
      for (const id of developmentAreaIds) {
        const body: CreateUnitOverrideRequest = {
          unit_id: id,
          action: 'remove',
        };
        const res = await post<
          UnitOverride,
          { errors?: { message: string }[] }
        >(basePath, body, new AbortController());
        if (res.type !== 'success' && firstError === undefined) {
          firstError =
            res.type === 'error' ? res.data?.errors?.[0]?.message : res.message;
        }
      }
      // Single refresh for the whole batch — one re-GET + one dev-areas call.
      // pending clears when that re-fetch settles (see useDevelopmentAreasQuery).
      await fanout({ refreshOverlaps: true });
      return firstError === undefined
        ? { ok: true }
        : { ok: false, message: firstError };
    },
    [basePath, fanout, setPending]
  );
};

// DELETE a specific action (rotate toggle-off).
export const useUndoUnitOverrideMutation = (
  workspace: TypeCurveWorkspaceResponse | null
) => {
  const fanout = useRefreshFanout(workspace);
  const setPending = useSetAtom(tcUnitResolutionPendingAtom);
  const setRotationPending = useSetAtom(tcUnitRotationPendingAtom);
  const basePath = unitActionsPath(workspace);

  return useCallback(
    async (
      overrideId: number,
      { refreshOverlaps = true }: { refreshOverlaps?: boolean } = {}
    ): Promise<MutationResult> => {
      if (!basePath) return { ok: false };
      // Un-rotate passes refreshOverlaps:false → uses the rotation atom (no
      // dev-areas re-fetch), cleared here. A refreshing undo would clear via
      // the dev-areas refresh effect, like remove.
      const setBusy = refreshOverlaps ? setPending : setRotationPending;
      setBusy(true);
      const res = await deleteReq<null, { errors?: { message: string }[] }>(
        `${basePath}/${overrideId}`,
        {},
        new AbortController()
      );
      if (res.type === 'success') {
        await fanout({ refreshOverlaps });
        if (!refreshOverlaps) setBusy(false);
        return { ok: true };
      }
      setBusy(false);
      return {
        ok: false,
        message:
          res.type === 'error' ? res.data?.errors?.[0]?.message : res.message,
      };
    },
    [basePath, fanout, setPending, setRotationPending]
  );
};

// POST undo_last_removal (pill Undo).
export const useUndoLastRemovalMutation = (
  workspace: TypeCurveWorkspaceResponse | null
) => {
  const fanout = useRefreshFanout(workspace);
  const setPending = useSetAtom(tcUnitResolutionPendingAtom);
  const basePath = unitActionsPath(workspace);

  return useCallback(async (): Promise<MutationResult> => {
    if (!basePath) return { ok: false };
    setPending(true);
    const res = await post<UnitOverride, { errors?: { message: string }[] }>(
      `${basePath}/undo_last_removal`,
      {},
      new AbortController()
    );
    if (res.type === 'success') {
      await fanout();
      return { ok: true };
    }
    setPending(false);
    return {
      ok: false,
      message:
        res.type === 'error' ? res.data?.errors?.[0]?.message : res.message,
    };
  }, [basePath, fanout, setPending]);
};
